/** One effect a species' `.msm` hangs on its body, as
 *  `tools/build_attached_effects.py` writes it: the bone it hangs on (null for
 *  the root), its effect key, and the bone's bind offset in the model frame,
 *  in millimetres. */
export interface AttachedEffectRow {
  readonly bone: string | null;
  readonly effect: string;
  readonly offsetMm: readonly number[];
}

export const ATTACHED_EFFECTS_URL = "creaturemesh/attached.json";

/** The follow id one boned row of one body's attached effects rides, apart
 *  from the body's own id and its hair's (`hairEntityId`). Entity ids stay
 *  under 2^24 and a species attaches under sixteen rows. */
export function attachedEffectFollowId(entityId: number, row: number): number {
  return 0x20000000 | ((row & 0x0f) << 24) | (entityId & 0x00ffffff);
}

/** `attached.json`'s rows by species name, or an empty table for a body that
 *  holds none. */
export function attachedEffectTable(body: unknown): ReadonlyMap<string, readonly AttachedEffectRow[]> {
  const species = (body as { species?: Record<string, AttachedEffectRow[]> } | null)?.species;
  return new Map(Object.entries(species ?? {}));
}

/** The table by species name, or an empty one when the file is absent or
 *  unreadable: a body without its attached effects is still drawn. */
export async function loadAttachedEffects(
  fetchJson: (url: string) => Promise<Response> = (url) => fetch(url),
): Promise<ReadonlyMap<string, readonly AttachedEffectRow[]>> {
  try {
    const response = await fetchJson(ATTACHED_EFFECTS_URL);
    if (!response.ok) return new Map();
    return attachedEffectTable(await response.json());
  } catch {
    return new Map();
  }
}
